import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiBaseUrl } from '@/config/production';
import { SessionManager } from '@/utils/sessionManager';

// Obter URL base e garantir que não tenha barra final para evitar barras duplas
const getApiBase = () => {
  const base = getApiBaseUrl();
  // Remove barras finais para evitar //api/auth/login
  return base.replace(/\/+$/, '');
};

const API_BASE = getApiBase();

import { apiRequest } from '../services/queryClient';

interface User {
  id: string;
  name: string;
  fullName?: string;
  email: string;
  role: 'admin' | 'photographer' | 'athlete';
  isApproved?: boolean;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isAdmin: boolean;
  isPhotographer: boolean;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<any>;
  register: (userData: RegisterData) => Promise<any>;
  logout: () => void;
  clearInvalidSession: () => void;
}

interface RegisterData {
  fullName: string;
  email: string;
  password: string;
  cpf?: string;
  phone?: string;
  userType: 'Athlete' | 'Photographer';
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.role === 'admin';
  const isPhotographer = user?.role === 'photographer' && (user?.isApproved ?? false);
  const isLoggedIn = !!user;

  // Função para tentar refresh do token
  const tryRefreshToken = async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('fotup_refresh_token');


      if (!refreshToken) {

        return false;
      }


      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: refreshToken })
      });

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.data?.session) {
          SessionManager.saveTokensDirectly(data.data.session.access_token, data.data.session.refresh_token);
          return true;
        } else {
          console.log('❌ tryRefreshToken - Estrutura de resposta inválida:', data);
        }
      } else {
        const errorData = await response.json();
      }

      return false;
    } catch (error) {
      console.error('❌ tryRefreshToken - Erro na requisição:', error);
      return false;
    }
  };

  // Verificar se há token salvo e validar
  useEffect(() => {

    const checkAuth = async () => {
      try {
        const token = SessionManager.getToken();
        const refreshToken = SessionManager.getRefreshToken();

        if (!token) {

          setUser(null);
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}/api/auth/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });


        if (response.ok) {
          const data = await response.json();

          // Backend retorna: { success: true, data: AuthUser } ou { success: true, data: { user: AuthUser } }
          const userData = data.user || data.data;

          setUser(userData);
        } else if (response.status === 401) {

          // Token expirado, tentar renovar
          const refreshSuccess = await tryRefreshToken();

          if (refreshSuccess) {
            // Tentar novamente com o novo token
            const newToken = SessionManager.getToken();
            const retryResponse = await fetch(`${API_BASE}/api/auth/me`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${newToken}`,
                'Content-Type': 'application/json'
              }
            });

            if (retryResponse.ok) {
              const retryData = await retryResponse.json();
              const userData = retryData.user || retryData.data;

              setUser(userData);
            } else {

              // Falha mesmo com novo token, limpar tudo
              SessionManager.clearTokensDirectly();
              setUser(null);
            }
          } else {

            // Não foi possível renovar, limpar localStorage
            SessionManager.clearTokensDirectly();
            setUser(null);
          }
        } else {

          // Outro erro, limpar localStorage
          SessionManager.clearTokensDirectly();
          setUser(null);
        }
      } catch (error) {
        console.error('❌ checkAuth - Erro na verificação:', error);
        SessionManager.clearTokensDirectly();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      // Verificar se a resposta é JSON válido
      let responseLogin;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            responseLogin = JSON.parse(text);
          } catch (parseError) {
            console.error('Erro ao fazer parse do JSON:', parseError);
            throw new Error('Resposta inválida do servidor');
          }
        } else {
          throw new Error('Resposta vazia do servidor');
        }
      } else {
        const text = await response.text();
        throw new Error(`Erro ${response.status}: ${text || 'Resposta não é JSON'}`);
      }

      if (!response.ok) {
        throw new Error(responseLogin.message || 'Erro ao fazer login');
      }

      // Backend retorna: { success: true, message: string, data: { user, session } }
      // Salvar token da sessão e dados do usuário


      setUser(responseLogin.data.user);

      // Salvar sessão compartilhada para outras aplicações
      SessionManager.saveSession(responseLogin.data.user, responseLogin.data.session.access_token, responseLogin.data.session.refresh_token);

      return responseLogin.data;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao criar conta');
      }

      // Se o registro foi bem-sucedido e há uma sessão, fazer login automático
      if (data.data?.session) {
        SessionManager.saveTokensDirectly(data.data.session.access_token, data.data.session.refresh_token);
        setUser(data.data.user);

        // Salvar sessão compartilhada para outras aplicações
        SessionManager.saveSession(data.data.user, data.data.session.access_token, data.data.session.refresh_token);
      }

      return data.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      const token = SessionManager.getToken();
      if (token) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      // Sempre limpar dados locais
      SessionManager.clearTokensDirectly();
      setUser(null);

      // Limpar sessão compartilhada
      SessionManager.clearSession();
    }
  };

  // Função para limpar sessão quando token é inválido
  const clearInvalidSession = () => {
    SessionManager.clearTokensDirectly();
    setUser(null);
    SessionManager.clearSession();
  };

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      isAdmin,
      isPhotographer,
      isLoggedIn,
      isLoading,
      login,
      register,
      logout,
      clearInvalidSession
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

// Hook para simular login (para desenvolvimento)
export function useAuth() {
  const { setUser, login } = useUser();

  const loginAsAdmin = async () => {
    try {
      await login('admin@fotup.com', 'admin123');
    } catch (error) {
      console.error('Erro ao fazer login como admin:', error);
    }
  };

  const loginAsPhotographer = async () => {
    try {
      await login('photographer@fotup.com', 'photo123');
    } catch (error) {
      console.error('Erro ao fazer login como fotógrafo:', error);
    }
  };

  const logout = () => {
    setUser(null);
  };

  return {
    loginAsAdmin,
    loginAsPhotographer,
    logout
  };
}